import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { t, fmt } from "./i18n.js";

// ---------------------------------------------------------------- data hook
export function useLoad(fn, deps = []) {
  const [state, set] = useState({ loading: true, data: null, error: null });
  const seq = useRef(0);
  const reload = useCallback(async () => {
    const my = ++seq.current;
    set((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await fn();
      if (my === seq.current) set({ loading: false, data, error: null });
    } catch (error) {
      if (my === seq.current) set({ loading: false, data: null, error });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { reload(); }, [reload]);
  return { ...state, reload, setData: (d) => set((s) => ({ ...s, data: typeof d === "function" ? d(s.data) : d })) };
}

/** Renders loading / error / empty states consistently. */
export function Loader({ state, isEmpty, emptyView, children }) {
  if (state.loading && !state.data) return <div className="state" role="status"><span className="spinner" />{t("loading")}</div>;
  if (state.error) {
    return (
      <div className="state state-error" role="alert">
        <strong>{state.error.status === 0 ? "لا يوجد اتصال" : "تعذر التحميل"}</strong>
        <span>{state.error.message}</span>
        <button className="btn" onClick={state.reload}>{t("retry")}</button>
      </div>
    );
  }
  if (isEmpty && isEmpty(state.data)) return emptyView || <div className="state">{t("empty")}</div>;
  return children(state.data);
}

export function Empty({ title, children }) {
  return <div className="state"><strong>{title}</strong>{children}</div>;
}

// ---------------------------------------------------------------- toasts
const ToastCtx = createContext(() => {});
export const useToast = () => useContext(ToastCtx);
export function ToastHost({ children }) {
  const [items, setItems] = useState([]);
  const push = useCallback((text, kind = "ok") => {
    const id = Math.random();
    setItems((x) => [...x, { id, text, kind }]);
    setTimeout(() => setItems((x) => x.filter((i) => i.id !== id)), kind === "error" ? 7000 : 3500);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite">
        {items.map((i) => <div key={i.id} className={"toast toast-" + i.kind}>{i.text}</div>)}
      </div>
    </ToastCtx.Provider>
  );
}

/**
 * useAction: runs a server action; success is shown only after the server confirmed it.
 * The idempotency key is kept across retries of the same action.
 */
export function useAction() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const run = useCallback(async (fn, okText) => {
    setBusy(true);
    setError(null);
    try {
      const r = await fn();
      if (okText) toast(okText, "ok");
      return r;
    } catch (e) {
      setError(e);
      toast(e.message, "error");
      throw e;
    } finally {
      setBusy(false);
    }
  }, [toast]);
  return { busy, error, run, setError };
}

export function ErrorBox({ error }) {
  if (!error) return null;
  return (
    <div className="errbox" role="alert">
      <div>{error.message}</div>
      {Array.isArray(error.details) && (
        <ul>{error.details.map((d, i) => <li key={i}>{d.message}{d.field ? ` (${d.field})` : ""}</li>)}</ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- primitives
export function Modal({ title, onClose, children, wide }) {
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="modal-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={"modal" + (wide ? " modal-wide" : "")} role="dialog" aria-modal="true" aria-label={title}>
        <header><h2>{title}</h2><button className="icon-btn" onClick={onClose} aria-label={t("close")}>✕</button></header>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, hint, children, error }) {
  return (
    <label className={"field" + (error ? " field-error" : "")}>
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

export function StatusPill({ status }) {
  return <span className={"pill pill-" + status}>{t("status." + status)}</span>;
}
export function PriorityTag({ priority }) {
  if (priority === "normal") return null;
  return <span className={"prio prio-" + priority}>{t("priority." + priority)}</span>;
}

export function Due({ task }) {
  const adj = task.adjusted_due_at || task.due_at;
  const moved = task.adjusted_due_at && task.adjusted_due_at !== task.due_at && task.excluded_seconds !== 0;
  return (
    <span className={"due" + (task.overdue ? " due-late" : "")}>
      {task.overdue ? "متأخرة · " : ""}{fmt.dt(adj)}
      {moved ? <small> (الأصلي {fmt.dt(task.due_at)})</small> : null}
    </span>
  );
}

export function Pager({ page, total, size, onPage }) {
  const pages = Math.max(1, Math.ceil(total / size));
  if (pages <= 1) return null;
  return (
    <nav className="pager" aria-label="الصفحات">
      <button className="btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>السابق</button>
      <span>صفحة {page} من {pages} — {total} عنصر</span>
      <button className="btn" disabled={page >= pages} onClick={() => onPage(page + 1)}>التالي</button>
    </nav>
  );
}

export function Progress({ value, label }) {
  const v = value == null ? null : Math.max(0, Math.min(100, value));
  return (
    <div className="progress" role="progressbar" aria-valuenow={v ?? undefined} aria-valuemin={0} aria-valuemax={100}
         aria-label={label}>
      <div className="progress-bar" style={{ inlineSize: (v ?? 0) + "%" }} />
      <span>{v == null ? "لا توجد مهام" : v + "%"}</span>
    </div>
  );
}

export function Confirm({ title, text, actionLabel, danger, needReason, onConfirm, onClose }) {
  const [reason, setReason] = useState("");
  const { busy, error, run } = useAction();
  return (
    <Modal title={title} onClose={onClose}>
      {text && <p>{text}</p>}
      {needReason && (
        <Field label="السبب (إلزامي)">
          <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      )}
      <ErrorBox error={error} />
      <div className="actions">
        <button className={"btn " + (danger ? "btn-danger" : "btn-primary")} disabled={busy || (needReason && reason.trim().length < 3)}
                onClick={() => run(() => onConfirm(reason)).then(onClose, () => {})}>
          {busy ? "جارٍ التنفيذ…" : actionLabel}
        </button>
        <button className="btn" onClick={onClose}>{t("cancel")}</button>
      </div>
    </Modal>
  );
}

export function Tabs({ tabs, value, onChange }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map(([k, label, badge]) => (
        <button key={k} role="tab" aria-selected={value === k} className={"tab" + (value === k ? " tab-on" : "")}
                onClick={() => onChange(k)}>
          {label}{badge ? <span className="badge">{badge}</span> : null}
        </button>
      ))}
    </div>
  );
}
